'use client';

import React from 'react';
import { useTheme } from 'styled-components';
import type { StopVariant } from '../MapStopPin/MapStopPin';

export interface RoutePoint {
  x: number;
  y: number;
}

export interface MapRouteProps extends Omit<React.SVGAttributes<SVGSVGElement>, 'points'> {
  /** Ordered list of SVG coordinate points along the route. */
  points: RoutePoint[];
  /** Activity type drives route colour. Default 'pickup'. */
  variant?: StopVariant;
  /** Render a dashed stroke (e.g. for deadhead segments). */
  dashed?: boolean;
  /** Stroke width in px. Default 3. */
  strokeWidth?: number;
}

/**
 * SVG polyline connecting map stops. Colour-coded by activity `variant`.
 * Use `dashed` for deadhead or non-service segments.
 *
 * @example
 * <MapRoute
 *   points={[{ x: 0, y: 0 }, { x: 100, y: 50 }, { x: 200, y: 20 }]}
 *   variant="pickup"
 * />
 */
export function MapRoute({
  points,
  variant = 'pickup',
  dashed = false,
  strokeWidth = 3,
  'aria-label': ariaLabel,
  ...rest
}: MapRouteProps) {
  const theme = useTheme();
  const color = theme.colors.activity[variant].bar;

  const ptStr = points.map((p) => `${p.x},${p.y}`).join(' ');

  // Compute bounding box for the SVG viewport
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const pad = strokeWidth * 2;
  const minX = points.length ? Math.min(...xs) - pad : 0;
  const minY = points.length ? Math.min(...ys) - pad : 0;
  const maxX = points.length ? Math.max(...xs) + pad : 100;
  const maxY = points.length ? Math.max(...ys) + pad : 10;
  const vbW = maxX - minX || 100;
  const vbH = maxY - minY || 10;

  const label = ariaLabel ?? `Route segment, ${variant}, ${points.length} waypoints`;

  return (
    <svg
      role="img"
      aria-label={label}
      width={vbW}
      height={vbH}
      viewBox={`${minX} ${minY} ${vbW} ${vbH}`}
      overflow="visible"
      {...rest}
    >
      {points.length > 0 && (
        <polyline
          points={ptStr}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={dashed ? `${strokeWidth * 3} ${strokeWidth * 2}` : undefined}
        />
      )}
    </svg>
  );
}
